/**
 * Assistant conversation and inline-form endpoints.
 *
 * Thin wrappers over the generated SDK, in the same shape as
 * `@/features/tickets/api/operations`: each one unwraps `{ data }` and returns
 * the response body, so the query hooks in `./hooks` stay free of SDK plumbing.
 *
 * These used to hand-build requests through the configured client because the
 * backend routes were being written in parallel and the SDK did not have them.
 * It does now, so the URLs, path parameters, query shapes and response types all
 * come from `openapi/openapi.json` — nothing here knows a host, a port, a path
 * or a field name that the server did not declare.
 *
 * The client is configured with `throwOnError: true` (see `@/lib/api/client`),
 * so a non-2xx rejects with `{ status, body }` and never reaches a `return`.
 *
 * Nothing here touches React.
 */

import {
  cancelAssistantMessage as sdkCancelAssistantMessage,
  createConversation as sdkCreateConversation,
  deleteConversation as sdkDeleteConversation,
  getAssistantMessage as sdkGetAssistantMessage,
  getConversation as sdkGetConversation,
  listConversations as sdkListConversations,
  postAssistantMessage as sdkPostAssistantMessage,
} from "@/lib/api";
import { client } from "@/lib/api/client";
import { logger } from "@/lib/logger";

import type {
  AssistantConversationCreateRequest,
  AssistantConversationDetail,
  AssistantConversationListResponse,
  AssistantConversationSummary,
  AssistantMessage,
  AssistantMessageAcceptedResponse,
  AssistantMessageCreateRequest,
  DeletionResponse,
  ServiceRequestDraft,
  ServiceRequestPrepareRequest,
  ServiceRequestSubmission,
  ServiceRequestSubmitRequest,
  TicketRef,
} from "./contract";

/**
 * A fresh `Idempotency-Key` for a state-changing POST.
 *
 * Required by the contract on `createTicket`, optional on
 * `postAssistantMessage`. `crypto.randomUUID` is available in every browser
 * this app targets and, unlike a timestamp, cannot collide across two tabs.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

// ─── §4 Assistant conversations ──────────────────────────────────────────

/** `POST /assistant/conversations` */
export async function createConversation(
  body: AssistantConversationCreateRequest,
): Promise<AssistantConversationSummary> {
  const { data } = await sdkCreateConversation({ body, throwOnError: true });
  return data;
}

/** `GET /assistant/conversations` */
export async function listConversations(params: {
  limit?: number;
  offset?: number;
  q?: string;
}): Promise<AssistantConversationListResponse> {
  const { data } = await sdkListConversations({ query: params, throwOnError: true });
  return data;
}

/** `GET /assistant/conversations/{conversation_id}` */
export async function getConversation(
  conversationId: string,
  params: { limit?: number; offset?: number; translate_to?: string } = {},
): Promise<AssistantConversationDetail> {
  const { data } = await sdkGetConversation({
    path: { conversation_id: conversationId },
    query: params,
    throwOnError: true,
  });
  return data;
}

/** `DELETE /assistant/conversations/{conversation_id}` — soft delete. */
export async function deleteConversation(conversationId: string): Promise<DeletionResponse> {
  const { data } = await sdkDeleteConversation({
    path: { conversation_id: conversationId },
    throwOnError: true,
  });
  return data;
}

/**
 * `POST /assistant/conversations/{conversation_id}/messages`
 *
 * Returns `202` immediately with the reserved assistant message and the
 * `stream_path` to open. It never blocks on the model.
 */
export async function postAssistantMessage(
  conversationId: string,
  body: AssistantMessageCreateRequest,
  idempotencyKey?: string,
): Promise<AssistantMessageAcceptedResponse> {
  const { data } = await sdkPostAssistantMessage({
    path: { conversation_id: conversationId },
    headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
    body,
    throwOnError: true,
  });
  return data;
}

/** `POST /assistant/conversations/{cid}/messages/{mid}/cancel` — idempotent. */
export async function cancelAssistantMessage(
  conversationId: string,
  messageId: string,
): Promise<AssistantMessage> {
  const { data } = await sdkCancelAssistantMessage({
    path: { conversation_id: conversationId, message_id: messageId },
    throwOnError: true,
  });
  return data;
}

/** `GET /assistant/conversations/{cid}/messages/{mid}` — polling fallback for SSE. */
export async function getAssistantMessage(
  conversationId: string,
  messageId: string,
  params: { translate_to?: string } = {},
): Promise<AssistantMessage> {
  const { data } = await sdkGetAssistantMessage({
    path: { conversation_id: conversationId, message_id: messageId },
    query: params,
    throwOnError: true,
  });
  return data;
}

// ─── §4.3 Inline request forms ───────────────────────────────────────────
//
// PENDING BACKEND, exactly as flagged on the types in `./contract`. The two
// calls below go through the same configured client as everything else — same
// `/api` prefix, same `{ status, body }` error envelope, same interceptors —
// but they name their own URL because `openapi/openapi.json` does not describe
// them yet. Replace both bodies with the generated `prepareServiceRequest` /
// `submitServiceRequest` SDK functions as soon as they exist; nothing outside
// this block knows a path.

/** `POST /assistant/service-requests/prepare` */
export const SERVICE_REQUEST_PREPARE_PATH = "/assistant/service-requests/prepare";
/** `POST /assistant/service-requests/submit` */
export const SERVICE_REQUEST_SUBMIT_PATH = "/assistant/service-requests/submit";

/**
 * Validate a filled-in offering form and hold it for confirmation.
 *
 * Writes nothing. A 200 with `field_errors` is the *expected* unhappy path —
 * the server re-checks every answer against the offering's own definition, so
 * a stale option or a parameter this build predates comes back attached to the
 * field it belongs to rather than as one opaque failure.
 */
export async function prepareServiceRequest(
  body: ServiceRequestPrepareRequest,
): Promise<ServiceRequestDraft> {
  const { data } = await client.post<{ 200: ServiceRequestDraft }, unknown, true>({
    url: SERVICE_REQUEST_PREPARE_PATH,
    body,
    throwOnError: true,
  });
  return data;
}

/**
 * File a prepared request.
 *
 * `Idempotency-Key` is sent for the same reason `createTicket` sends one: a
 * user who presses the button twice, or a browser that retries the POST, must
 * not raise two tickets. The card additionally goes read-only on success, so
 * this is the second line of defence rather than the first.
 */
export async function submitServiceRequest(
  body: ServiceRequestSubmitRequest,
  idempotencyKey?: string,
): Promise<ServiceRequestSubmission> {
  const { data } = await client.post<{ 200: unknown }, unknown, true>({
    url: SERVICE_REQUEST_SUBMIT_PATH,
    headers: { "Idempotency-Key": idempotencyKey ?? newIdempotencyKey() },
    body,
    throwOnError: true,
  });
  return normaliseSubmission(data);
}

/**
 * Accept either `{ ticket: TicketRef }` or a bare `TicketRef`.
 *
 * The rest of the API returns ticket references both ways — some responses
 * wrap them, `createTicket` returns the thing itself — and this
 * endpoint's envelope is not pinned down until it appears in the schema. One
 * `in` check here is cheaper than a runtime `undefined` in the success card.
 */
function normaliseSubmission(data: unknown): ServiceRequestSubmission {
  if (typeof data === "object" && data !== null && "ticket" in data) {
    return data as ServiceRequestSubmission;
  }
  logger.debug("[assistant] service request submitted as a bare ticket ref");
  return { ticket: data as TicketRef };
}
